import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useTenantCatalog } from '../hooks';
import { StatusBadge } from '../components/StatusBadge';
import { Icon } from '../shared/Icon';
import type { Change, ChecklistItem, Comment, Issue, IssueLink, Page, Transition } from '../types';

/** Карточка задачи: поля, переходы воркфлоу, комментарии, чеклист, связи и история. */
export function IssuePage() {
  const { tenantId = '', issueKey = '' } = useParams();
  const catalog = useTenantCatalog(tenantId);
  const queryClient = useQueryClient();

  const issue = useQuery({
    queryKey: ['issue', tenantId, issueKey],
    queryFn: () => api.get<Issue>(`/tenants/${tenantId}/issues/by-key/${issueKey}`),
  });
  const issueId = issue.data?.id;

  const transitions = useQuery({
    queryKey: ['transitions', issueId],
    queryFn: () => api.get<Transition[]>(`/tenants/${tenantId}/issues/${issueId}/transitions`),
    enabled: Boolean(issueId),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['issue', tenantId, issueKey] });
    await queryClient.invalidateQueries({ queryKey: ['transitions', issueId] });
    await queryClient.invalidateQueries({ queryKey: ['history', issueId] });
  };

  const move = useMutation({
    mutationFn: (statusId: string) =>
      api.post(`/tenants/${tenantId}/issues/${issueId}/transitions`, { statusId }),
    onSuccess: refresh,
  });

  if (issue.isLoading) return <div className="state">Загрузка…</div>;
  if (issue.isError) return <div className="state">{(issue.error as ApiError).message}</div>;

  const data = issue.data!;
  const queue = catalog.queueById.get(data.queueId);

  return (
    <>
      <div className="page-head">
        <div>
          {queue && (
            <Link className="small" to={`/t/${tenantId}/queues/${queue.id}`}>
              ← {queue.key} — {queue.name}
            </Link>
          )}
          <div className="issue-title-key">{data.key}</div>
          <h1>{data.summary}</h1>
        </div>
      </div>

      <div className="row wrap" style={{ marginBottom: 16 }}>
        <StatusBadge status={catalog.statusById.get(data.statusId)} />
        {transitions.data?.map((transition) => {
          const target = catalog.statusById.get(transition.toStatusId);
          return (
            <button
              key={transition.id}
              className="btn btn-secondary btn-sm"
              disabled={move.isPending}
              onClick={() => move.mutate(transition.toStatusId)}
            >
              {transition.name || target?.name || 'Перевести'}
            </button>
          );
        })}
        {move.isError && <span className="form-error">{(move.error as ApiError).message}</span>}
      </div>

      <div className="issue-grid">
        <div className="col">
          <div className="card">
            <h3>Описание</h3>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
              {data.description || <span className="muted">не заполнено</span>}
            </div>
          </div>

          <Checklist tenantId={tenantId} issueId={data.id} />
          <Comments tenantId={tenantId} issueId={data.id} />
          <Links tenantId={tenantId} issueId={data.id} />
          <History tenantId={tenantId} issueId={data.id} />
        </div>

        <aside className="card">
          <dl className="props">
            <dt>Очередь</dt>
            <dd>{queue?.name ?? '—'}</dd>
            <dt>Тип</dt>
            <dd>{catalog.typeById.get(data.typeId)?.name ?? '—'}</dd>
            <dt>Приоритет</dt>
            <dd>{data.priorityId ? catalog.priorityById.get(data.priorityId)?.name ?? '—' : '—'}</dd>
            <dt>Исполнитель</dt>
            <dd>{data.assigneeId ? shortId(data.assigneeId) : <span className="muted">не назначен</span>}</dd>
            <dt>Автор</dt>
            <dd>{shortId(data.authorId)}</dd>
            <dt>Наблюдатели</dt>
            <dd>{data.watchers.length}</dd>
            {data.resolvedAt && (
              <>
                <dt>Завершена</dt>
                <dd>{new Date(data.resolvedAt).toLocaleString('ru-RU')}</dd>
              </>
            )}
            <dt>Создана</dt>
            <dd>{new Date(data.created).toLocaleString('ru-RU')}</dd>
          </dl>
        </aside>
      </div>
    </>
  );
}

function Checklist({ tenantId, issueId }: { tenantId: string; issueId: string }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const key = ['checklist', issueId];
  const base = `/tenants/${tenantId}/issues/${issueId}/checklist`;

  const items = useQuery({ queryKey: key, queryFn: () => api.get<ChecklistItem[]>(base) });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });

  const add = useMutation({
    mutationFn: () => api.post(base, { text }),
    onSuccess: () => {
      setText('');
      return invalidate();
    },
  });
  const toggle = useMutation({
    mutationFn: (item: ChecklistItem) => api.patch(`${base}/${item.id}/done`, { done: !item.done }),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: (id: string) => api.del(`${base}/${id}`), onSuccess: invalidate });

  const done = items.data?.filter((item) => item.done).length ?? 0;
  const total = items.data?.length ?? 0;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Чеклист</h2>
        {total > 0 && (
          <span className="muted small">
            {done} из {total}
          </span>
        )}
      </div>

      {total > 0 && (
        <div className="progress-track" style={{ marginBottom: 10 }}>
          <div className="progress-fill" style={{ width: `${(done / total) * 100}%` }} />
        </div>
      )}

      <ul className="checklist">
        {items.data?.map((item) => (
          <li key={item.id} className={item.done ? 'done' : undefined}>
            <input type="checkbox" checked={item.done} onChange={() => toggle.mutate(item)} />
            <span className="checklist-text">{item.text}</span>
            <button
              className="btn btn-secondary btn-xs"
              onClick={() => remove.mutate(item.id)}
              title="Удалить пункт"
            >
              <Icon name="trash" size={12} />
            </button>
          </li>
        ))}
      </ul>

      <div className="row" style={{ marginTop: 10 }}>
        <input
          className="input"
          placeholder="Новый пункт"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && text.trim()) add.mutate();
          }}
        />
        <button
          className="btn btn-secondary"
          disabled={!text.trim() || add.isPending}
          onClick={() => add.mutate()}
        >
          Добавить
        </button>
      </div>
    </div>
  );
}

function Comments({ tenantId, issueId }: { tenantId: string; issueId: string }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const key = ['comments', issueId];
  const base = `/tenants/${tenantId}/issues/${issueId}/comments`;

  const comments = useQuery({ queryKey: key, queryFn: () => api.get<Page<Comment>>(`${base}?size=100`) });
  const add = useMutation({
    mutationFn: () => api.post(base, { text }),
    onSuccess: () => {
      setText('');
      return queryClient.invalidateQueries({ queryKey: key });
    },
  });

  return (
    <div className="card">
      <div className="card-head">
        <h2>Комментарии</h2>
      </div>

      {comments.data?.content.length === 0 && (
        <div className="muted small">Пока никто ничего не написал</div>
      )}
      {comments.data?.content.map((comment) => (
        <div className="comment" key={comment.id}>
          <div className="comment-meta">
            {shortId(comment.authorId)} · {new Date(comment.created).toLocaleString('ru-RU')}
            {comment.edited && ' · изменён'}
          </div>
          <div className="comment-text">{comment.text}</div>
        </div>
      ))}

      <div style={{ marginTop: 12 }}>
        <textarea
          className="input"
          placeholder="Написать комментарий"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <div className="row" style={{ marginTop: 8 }}>
          <div className="spacer" />
          <button
            className="btn btn-primary"
            disabled={!text.trim() || add.isPending}
            onClick={() => add.mutate()}
          >
            Отправить
          </button>
        </div>
      </div>
    </div>
  );
}

function Links({ tenantId, issueId }: { tenantId: string; issueId: string }) {
  const links = useQuery({
    queryKey: ['links', issueId],
    queryFn: () => api.get<IssueLink[]>(`/tenants/${tenantId}/issues/${issueId}/links`),
  });

  if (!links.data || links.data.length === 0) return null;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Связи</h2>
      </div>
      {links.data.map((link) => (
        <div className="list-item" key={link.id}>
          <div>
            <span className="muted small">{LINK_LABELS[link.linkType] ?? link.linkType}</span>{' '}
            <Link to={`/t/${tenantId}/issues/${link.otherIssueKey}`}>{link.otherIssueKey}</Link>{' '}
            <span className="small">{link.otherIssueSummary}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function History({ tenantId, issueId }: { tenantId: string; issueId: string }) {
  const history = useQuery({
    queryKey: ['history', issueId],
    queryFn: () => api.get<Page<Change>>(`/tenants/${tenantId}/issues/${issueId}/history?size=50`),
  });

  return (
    <div className="card">
      <div className="card-head">
        <h2>История</h2>
      </div>
      <ul className="history-list">
        {history.data?.content.map((change) => (
          <li key={change.id}>
            <span className="muted">{new Date(change.created).toLocaleString('ru-RU')}</span>{' '}
            <span className="history-field">{FIELD_LABELS[change.field] ?? change.field}</span>
            {change.field === 'created' ? (
              <> — задача заведена</>
            ) : (
              <>
                : <span className="muted">{change.oldValue ?? '—'}</span> → {change.newValue ?? '—'}
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

const FIELD_LABELS: Record<string, string> = {
  created: 'Создание',
  status: 'Статус',
  summary: 'Заголовок',
  description: 'Описание',
  assignee: 'Исполнитель',
  priority: 'Приоритет',
  type: 'Тип',
  parent: 'Родитель',
  sprint: 'Спринт',
  dueDate: 'Срок',
};

const LINK_LABELS: Record<string, string> = {
  RELATES: 'связана с',
  BLOCKS: 'блокирует',
  IS_BLOCKED_BY: 'заблокирована',
  DUPLICATES: 'дублирует',
  IS_DUPLICATED_BY: 'дублируется',
  PRECEDES: 'предшествует',
  FOLLOWS: 'следует за',
};

/** Имён людей трекер не знает — только идентификаторы из subjects. */
const shortId = (id: string): string => id.slice(0, 8);
